// PersonDetailDrawer — the People-tab "Profile" drawer, opened by a row click or
// the row pencil. Reads a company user (profile, details, structure, roles, and a
// computed Effective access matrix) and lets the Details card flip to an editable
// form: name/department/employment/ID/dates/nationality plus GROUPS + TAGS
// pickers. Status is display-only (a chip). Save → PATCH /company-users/users/:id (structure
// placements and roles are editable — owner roles stay fixed and the roles
// picker is `edit`-gated; email/phone are read-only, not sent). Fetches
// GET /company-users/users/detail/:id.

import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import {
  Avatar,
  Button,
  Card,
  Chip,
  Dropdown,
  Drawer,
  Field,
  Icon,
  Input,
  SelectDropdown,
} from '@/components/common';
import {
  useGetPersonDetailQuery,
  useUpdatePersonMutation,
  type AccessModule,
} from '@/api/endpoints/peopleApi';
import { useGetGroupSectionsQuery } from '@/api/endpoints/groupsApi';
import { useGetPeopleTagsQuery } from '@/api/endpoints/tagsApi';
import {
  useGetRolesSelectionDetailedQuery,
  type RoleSelectionDetail,
} from '@/api/endpoints/rolesApi';
import { useGetStructureSelectionQuery } from '@/api/endpoints/structureApi';
import { useLevelConfig } from '@/api/endpoints/levelsApi';
import useModuleAccess from '@/hooks/useModuleAccess';
import type { OrgUnit } from '@/mocks/structure';
import { EMPLOYMENT_TYPES, NATIONALITIES } from '../shared/personFields';
import { PERSON_STATUS_META } from '../shared/personStatus';
import initialsOf from '@/utils/initials';
import { fmtDatePadded } from '@/utils/formatDate';
import { todayPlus } from '@/utils/date';

/** Flatten the org tree into a single list (same idiom as PeoplePage). */
const flattenUnits = (units: OrgUnit[]): OrgUnit[] =>
  units.flatMap((u) => [u, ...flattenUnits(u.children ?? [])]);

interface PersonDetailDrawerProps {
  open: boolean;
  personId?: string;
  onClose: () => void;
}

interface PersonDraft {
  firstName: string;
  lastName: string;
  department: string | null;
  employmentType: string | null;
  personalIdNumber: string | null;
  personalIdExpiry: string | null; // yyyy-mm-dd (native date input)
  birthDate: string | null; // yyyy-mm-dd
  nationality: string | null;
  groupIds: string[];
  tagIds: string[];
  roleIds: string[];
  nodeIds: string[];
}

const emptyDraft: PersonDraft = {
  firstName: '',
  lastName: '',
  department: null,
  employmentType: null,
  personalIdNumber: null,
  personalIdExpiry: null,
  birthDate: null,
  nationality: null,
  groupIds: [],
  tagIds: [],
  roleIds: [],
  nodeIds: [],
};

/** Union the selected roles' permission groups into the AccessModule[] shape the
 *  Effective-access matrix renders — deduped by module _id, permissions by _id. */
const mergeRoleAccess = (
  roles: RoleSelectionDetail[],
  key: 'adminPermissions' | 'userPermissions',
): AccessModule[] => {
  const byId = new Map<string, AccessModule>();
  for (const role of roles) {
    for (const group of role[key] ?? []) {
      let entry = byId.get(group.moduleId._id);
      if (!entry) {
        entry = { id: group.moduleId._id, title: group.moduleId.title, permissions: [] };
        byId.set(group.moduleId._id, entry);
      }
      const seen = new Set(entry.permissions.map((p) => p.id));
      for (const perm of group.permissions ?? []) {
        if (seen.has(perm._id)) continue;
        seen.add(perm._id);
        entry.permissions.push({ id: perm._id, title: perm.title });
      }
    }
  }
  return [...byId.values()];
};

/** yyyy-mm-dd (native date input) → ISO datetime; undefined when empty. */
const toIso = (d: string | null) => (d ? new Date(d).toISOString() : undefined);

/** ISO datetime → yyyy-mm-dd for a native date input; "" when empty/invalid. */
const toDateInput = (iso: string): string => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toISOString().slice(0, 10);
};

const sortedJoin = (a: string[]) => [...a].sort().join('|');

const PersonDetailDrawer = ({ open, personId, onClose }: PersonDetailDrawerProps) => {
  const { t } = useTranslation();
  const { data: detail, isFetching } = useGetPersonDetailQuery(personId as string, {
    skip: !open || !personId,
    refetchOnMountOrArgChange: true,
  });
  const [updatePerson, { isLoading: saving }] = useUpdatePersonMutation();
  const { data: groupSections = [] } = useGetGroupSectionsQuery(undefined, { skip: !open });
  const { data: peopleTags = [] } = useGetPeopleTagsQuery(undefined, { skip: !open });
  const { data: detailedRoles = [] } = useGetRolesSelectionDetailedQuery(undefined, {
    skip: !open,
  });
  const { data: structure = [] } = useGetStructureSelectionQuery(undefined, { skip: !open });
  const levelConfig = useLevelConfig();

  // Without `edit` the drawer is a pure read surface: no Edit flip, no footer
  // actions, and the always-editable roles/structure pickers freeze too.
  const { canEdit } = useModuleAccess('people');

  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<PersonDraft>(emptyDraft);
  const [initial, setInitial] = useState<PersonDraft>(emptyDraft);
  const [groupPickerOpen, setGroupPickerOpen] = useState(false);
  const [tagPickerOpen, setTagPickerOpen] = useState(false);
  const [rolePickerOpen, setRolePickerOpen] = useState(false);
  const [nodePickerOpen, setNodePickerOpen] = useState(false);

  // On open, always start in read mode and clear transient picker state.
  useEffect(() => {
    if (open) {
      setEditing(false);
      setGroupPickerOpen(false);
      setTagPickerOpen(false);
      setRolePickerOpen(false);
      setNodePickerOpen(false);
    }
  }, [open]);

  // Seed draft + baseline from the fetched person (re-seeds after a save refetch).
  useEffect(() => {
    if (!detail) return;
    const seeded: PersonDraft = {
      firstName: detail.firstName,
      lastName: detail.lastName,
      department: detail.department || null,
      employmentType: detail.employmentType || null,
      personalIdNumber: detail.personalIdNumber || null,
      personalIdExpiry: toDateInput(detail.personalIdExpiry) || null,
      birthDate: toDateInput(detail.birthDate) || null,
      nationality: detail.nationality || null,
      groupIds: detail.groups.map((g) => g.id),
      tagIds: detail.tags.map((tg) => tg.id),
      roleIds: detail.roles.map((r) => r.id),
      nodeIds: detail.units.map((u) => u.id),
    };
    setDraft(seeded);
    setInitial(seeded);
  }, [detail]);

  const patch = (changes: Partial<PersonDraft>) => setDraft((d) => ({ ...d, ...changes }));

  const allGroups = useMemo(
    () => groupSections.flatMap((s) => s.groups.map((g) => ({ id: g.id, name: g.name }))),
    [groupSections],
  );
  // Name lookups for the selected chips — merge the person's own groups/tags
  // (source of truth for currently-assigned) with the available lists.
  const groupNameById = useMemo(() => {
    const m = new Map<string, string>();
    detail?.groups.forEach((g) => m.set(g.id, g.name));
    allGroups.forEach((g) => m.set(g.id, g.name));
    return m;
  }, [detail, allGroups]);
  const tagTitleById = useMemo(() => {
    const m = new Map<string, string>();
    detail?.tags.forEach((tg) => m.set(tg.id, tg.title));
    peopleTags.forEach((tg) => m.set(tg.id, tg.title));
    return m;
  }, [detail, peopleTags]);
  const roleNameById = useMemo(() => {
    const m = new Map<string, string>();
    detail?.roles.forEach((r) => m.set(r.id, r.name));
    detailedRoles.forEach((r) => m.set(r.id, r.name));
    return m;
  }, [detail, detailedRoles]);

  // Selectable structure nodes (company root/level 0 excluded, as elsewhere).
  type NodeOption = { id: string; name: string; levelName: string; levelIcon: string };
  const allNodes = useMemo<NodeOption[]>(
    () =>
      flattenUnits(structure)
        .filter((u) => u.level !== 0)
        .map((u) => ({
          id: u.id,
          name: u.name,
          levelName: levelConfig(u.level).name,
          levelIcon: levelConfig(u.level).icon,
        })),
    [structure, levelConfig],
  );
  const nodeById = useMemo(() => {
    const m = new Map<string, NodeOption>();
    detail?.units.forEach((u) =>
      m.set(u.id, { id: u.id, name: u.name, levelName: u.levelName, levelIcon: u.levelIcon }),
    );
    allNodes.forEach((n) => m.set(n.id, n));
    return m;
  }, [detail, allNodes]);

  const dirty =
    !!detail &&
    (draft.firstName !== initial.firstName ||
      draft.lastName !== initial.lastName ||
      draft.department !== initial.department ||
      draft.employmentType !== initial.employmentType ||
      draft.personalIdNumber !== initial.personalIdNumber ||
      draft.personalIdExpiry !== initial.personalIdExpiry ||
      draft.birthDate !== initial.birthDate ||
      draft.nationality !== initial.nationality ||
      sortedJoin(draft.groupIds) !== sortedJoin(initial.groupIds) ||
      sortedJoin(draft.tagIds) !== sortedJoin(initial.tagIds) ||
      sortedJoin(draft.roleIds) !== sortedJoin(initial.roleIds) ||
      sortedJoin(draft.nodeIds) !== sortedJoin(initial.nodeIds));

  // Owner users' roles are fixed (granted structurally) — no role editing.
  // Everyone else must keep at least one role.
  const isOwnerUser = !!detail?.roles.some((r) => r.isOwner);
  const rolesInvalid = !isOwnerUser && draft.roleIds.length === 0;
  // Birth date is past-only: yesterday is the latest allowed day.
  const maxBirthDate = todayPlus(-1);
  const birthDateInvalid = !!draft.birthDate && draft.birthDate > maxBirthDate;

  const toggleGroup = (id: string) =>
    patch({
      groupIds: draft.groupIds.includes(id)
        ? draft.groupIds.filter((x) => x !== id)
        : [...draft.groupIds, id],
    });
  const toggleTag = (id: string) =>
    patch({
      tagIds: draft.tagIds.includes(id)
        ? draft.tagIds.filter((x) => x !== id)
        : [...draft.tagIds, id],
    });
  const toggleRole = (id: string) =>
    patch({
      roleIds: draft.roleIds.includes(id)
        ? draft.roleIds.filter((x) => x !== id)
        : [...draft.roleIds, id],
    });
  const toggleNode = (id: string) =>
    patch({
      nodeIds: draft.nodeIds.includes(id)
        ? draft.nodeIds.filter((x) => x !== id)
        : [...draft.nodeIds, id],
    });

  const discard = () => {
    setDraft(initial);
    setEditing(false);
  };

  const save = async () => {
    if (!detail || !dirty || rolesInvalid || birthDateInvalid) return;
    try {
      await updatePerson({
        id: detail.id,
        body: {
          firstName: draft.firstName.trim(),
          lastName: draft.lastName.trim(),
          userRoles: draft.roleIds,
          companyStructureNodes: draft.nodeIds,
          tags: draft.tagIds,
          groups: draft.groupIds,
          userStatus: detail.status,
          // Optional fields → null when empty/cleared.
          department: draft.department?.trim() || null,
          employmentType: draft.employmentType,
          personalIdNumber: draft.personalIdNumber?.trim() || null,
          personalIdExpiry: toIso(draft.personalIdExpiry) ?? null,
          nationality: draft.nationality,
          birthDate: toIso(draft.birthDate) ?? null,
        },
      }).unwrap();
      toast.success(t('person_updated'));
      setEditing(false);
    } catch {
      toast.error(t('error_generic'));
    }
  };

  const loading = isFetching && !detail;
  const nodeNames = draft.nodeIds
    .map((id) => nodeById.get(id)?.name)
    .filter(Boolean)
    .join(', ');
  // Effective access is computed entirely client-side from the selected roles'
  // permissions (the detail response no longer carries it). Roles are editable
  // regardless of the Details edit toggle, so this updates live as they change.
  const selectedRoles = useMemo(
    () => detailedRoles.filter((r) => draft.roleIds.includes(r.id)),
    [detailedRoles, draft.roleIds],
  );
  const webAccess = mergeRoleAccess(selectedRoles, 'adminPermissions');
  const mobileAccess = mergeRoleAccess(selectedRoles, 'userPermissions');
  const rolesCount = detail ? draft.roleIds.length : 0;

  // One WEB / MOBILE access section — module rows, each a strip of permission pills.
  const renderAccess = (label: string, icon: string, modules: AccessModule[]) =>
    modules.length > 0 ? (
      <div className='person-detail__access-group'>
        <p className='person-detail__access-head'>
          <Icon name={icon} size={14} />
          {label}
        </p>
        {modules.map((m) => (
          <div key={m.id} className='person-detail__access-row'>
            <span className='person-detail__access-module'>{m.title}</span>
            <span className='person-detail__perms'>
              {m.permissions.map((p) => (
                <span key={p.id} className='person-detail__perm'>
                  <Icon name='check' size={13} />
                  {p.title}
                </span>
              ))}
            </span>
          </div>
        ))}
      </div>
    ) : null;

  // The GROUPS / TAGS chip-with-remove + dashed-picker editor (edit mode only).
  const renderChipPicker = (
    ids: string[],
    nameOf: Map<string, string>,
    options: { id: string; label: string }[],
    icon: string,
    open_: boolean,
    setOpen_: (v: boolean) => void,
    toggle: (id: string) => void,
    addLabel: string,
    removeKey: string,
    emptyText: string,
  ) => (
    <div className='person-detail__chips person-detail__chips--wrap'>
      {ids.map((id) => (
        <span key={id} className='person-detail__role-chip'>
          <Icon name={icon} size={16} />
          <span className='person-detail__chip-name'>{nameOf.get(id) ?? id}</span>
          <button
            type='button'
            className='person-detail__chip-x'
            aria-label={t(removeKey, { name: nameOf.get(id) ?? id })}
            onClick={() => toggle(id)}
          >
            <Icon name='x' size={14} />
          </button>
        </span>
      ))}
      <Dropdown
        open={open_}
        onClose={() => setOpen_(false)}
        align='start'
        portal
        className='person-detail__picker'
        trigger={
          <button
            type='button'
            className='people-role-pill people-role-pill--dashed'
            aria-expanded={open_}
            onClick={() => setOpen_(!open_)}
          >
            <Icon name='plus' size={15} />
            {addLabel}
          </button>
        }
      >
        <div className='person-detail__picker-list'>
          {options.length === 0 && <span className='person-detail__picker-empty'>{emptyText}</span>}
          {options.map((o) => {
            const active = ids.includes(o.id);
            return (
              <button
                key={o.id}
                type='button'
                role='menuitemcheckbox'
                aria-checked={active}
                className={`app-dropdown__item${active ? ' is-selected' : ''}`}
                onClick={() => toggle(o.id)}
              >
                <Icon name={icon} size={14} />
                <span className='app-dropdown__item-label'>{o.label}</span>
                <Icon name={active ? 'check' : 'plus'} size={16} />
              </button>
            );
          })}
        </div>
      </Dropdown>
    </div>
  );

  return (
    <Drawer
      open={open}
      onClose={onClose}
      className='person-detail'
      icon='user-round'
      title={t('people_detail_title')}
      footer={
        <div className='person-detail__footer'>
          {!dirty && (
            <span className='person-detail__saved'>
              <Icon name='check' size={15} />
              {t('guest_all_saved')}
            </span>
          )}
          {canEdit && (
            <span className='person-detail__footer-actions'>
              <Button variant='secondary' onClick={discard} disabled={!dirty || saving}>
                {t('guest_discard')}
              </Button>
              <Button
                iconStart='check'
                onClick={save}
                loading={saving}
                disabled={!dirty || rolesInvalid || birthDateInvalid}
              >
                {t('guest_save_changes')}
              </Button>
            </span>
          )}
        </div>
      }
    >
      {loading ? (
        <div className='person-detail__loading'>
          <Icon name='loader' size={26} />
          {t('text_loading')}
        </div>
      ) : (
        detail && (
          <div className='person-detail__body'>
            {/* ---- profile card ---- */}
            <Card className='person-detail__profile'>
              <Avatar initials={initialsOf(detail.fullName)} size={48} />
              <span className='person-detail__identity'>
                <span className='person-detail__name'>{detail.fullName}</span>
                {detail.email && <span className='person-detail__email'>{detail.email}</span>}
              </span>
              <span className='person-detail__status'>
                <Chip
                  variant={PERSON_STATUS_META[detail.status].variant}
                  dot={PERSON_STATUS_META[detail.status].dot}
                >
                  {t(PERSON_STATUS_META[detail.status].labelKey)}
                </Chip>
              </span>
            </Card>

            {/* ---- details card ---- */}
            <Card
              padded={false}
              head={
                <div className='person-detail__details-head'>
                  <span className='person-detail__details-title'>{t('people_detail_details')}</span>
                  {!editing && canEdit && (
                    <Button
                      variant='secondary'
                      iconStart='pencil'
                      size='sm'
                      onClick={() => setEditing(true)}
                    >
                      {t('action_edit')}
                    </Button>
                  )}
                </div>
              }
            >
              <div className='person-detail__details-body'>
                {editing ? (
                  <div className='person-detail__form'>
                    <div className='person-detail__row'>
                      <Field label={t('add_person_firstname')}>
                        <Input
                          value={draft.firstName}
                          placeholder={t('add_person_firstname_ph')}
                          onChange={(e) => patch({ firstName: e.target.value })}
                        />
                      </Field>
                      <Field label={t('add_person_lastname')}>
                        <Input
                          value={draft.lastName}
                          placeholder={t('add_person_lastname_ph')}
                          onChange={(e) => patch({ lastName: e.target.value })}
                        />
                      </Field>
                    </div>
                    <div className='person-detail__row'>
                      <Field label={t('guest_label_email')}>
                        <Input
                          type='email'
                          iconStart='mail'
                          value={detail.email}
                          disabled
                          readOnly
                        />
                      </Field>
                      <Field label={t('add_person_phone')}>
                        <Input iconStart='phone' value={detail.phone} dir='ltr' disabled readOnly />
                      </Field>
                    </div>
                    <div className='person-detail__row'>
                      <Field label={t('add_person_department')}>
                        <Input
                          value={draft.department ?? ''}
                          placeholder={t('add_person_department_ph')}
                          onChange={(e) => patch({ department: e.target.value || null })}
                        />
                      </Field>
                      <Field label={t('add_person_emptype')} htmlFor='person-emptype'>
                        <SelectDropdown
                          id='person-emptype'
                          size='lg'
                          value={draft.employmentType}
                          onChange={(next) => patch({ employmentType: next })}
                          options={[
                            { value: null, label: t('add_person_emptype_ph') },
                            ...EMPLOYMENT_TYPES.map((v) => ({ value: v, label: v })),
                          ]}
                        />
                      </Field>
                    </div>
                    <div className='person-detail__row'>
                      <Field label={t('add_person_idnumber')}>
                        <Input
                          iconStart='id-card'
                          value={draft.personalIdNumber ?? ''}
                          placeholder={t('add_person_idnumber_ph')}
                          onChange={(e) => patch({ personalIdNumber: e.target.value || null })}
                        />
                      </Field>
                      <Field label={t('add_person_idexpiry')}>
                        <Input
                          type='date'
                          iconStart='calendar'
                          value={draft.personalIdExpiry ?? ''}
                          onChange={(e) => patch({ personalIdExpiry: e.target.value || null })}
                        />
                      </Field>
                    </div>
                    <div className='person-detail__row'>
                      <Field
                        label={t('add_person_birthdate')}
                        error={birthDateInvalid ? t('add_person_err_birthdate') : undefined}
                      >
                        <Input
                          type='date'
                          iconStart='calendar'
                          max={maxBirthDate}
                          error={birthDateInvalid}
                          value={draft.birthDate ?? ''}
                          onChange={(e) => patch({ birthDate: e.target.value || null })}
                        />
                      </Field>
                      <Field label={t('add_person_nationality')} htmlFor='person-nationality'>
                        <SelectDropdown
                          id='person-nationality'
                          size='lg'
                          value={draft.nationality}
                          onChange={(next) => patch({ nationality: next })}
                          options={[
                            { value: null, label: t('add_person_nationality_ph') },
                            ...NATIONALITIES.map((n) => ({ value: n, label: n })),
                          ]}
                        />
                      </Field>
                    </div>

                    <div className='person-detail__stack'>
                      <span className='person-detail__label'>{t('people_col_groups')}</span>
                      {renderChipPicker(
                        draft.groupIds,
                        groupNameById,
                        allGroups.map((g) => ({ id: g.id, label: g.name })),
                        'users',
                        groupPickerOpen,
                        setGroupPickerOpen,
                        toggleGroup,
                        t('people_detail_add_group'),
                        'people_detail_group_remove',
                        t('people_detail_groups_empty'),
                      )}
                    </div>
                    <div className='person-detail__stack'>
                      <span className='person-detail__label'>{t('people_detail_tags_label')}</span>
                      {renderChipPicker(
                        draft.tagIds,
                        tagTitleById,
                        peopleTags.map((tg) => ({ id: tg.id, label: tg.title })),
                        'tag',
                        tagPickerOpen,
                        setTagPickerOpen,
                        toggleTag,
                        t('people_detail_add_tag'),
                        'people_detail_tag_remove',
                        t('people_detail_tags_empty'),
                      )}
                    </div>
                  </div>
                ) : (
                  <>
                    <dl className='person-detail__dl'>
                      {[
                        { label: t('guest_label_email'), value: detail.email, ltr: true },
                        { label: t('add_person_phone'), value: detail.phone, ltr: true },
                        { label: t('add_person_department'), value: detail.department },
                        { label: t('add_person_emptype'), value: detail.employmentType },
                        { label: t('add_person_idnumber'), value: detail.personalIdNumber },
                        {
                          label: t('add_person_idexpiry'),
                          value: fmtDatePadded(detail.personalIdExpiry),
                        },
                        {
                          label: t('add_person_birthdate'),
                          value: fmtDatePadded(detail.birthDate),
                        },
                        { label: t('add_person_nationality'), value: detail.nationality },
                      ].map((f) => (
                        <div key={f.label} className='person-detail__field'>
                          <dt className='person-detail__label'>{f.label}</dt>
                          <dd
                            className='person-detail__value'
                            {...(f.ltr ? { dir: 'ltr' as const } : {})}
                          >
                            {f.value || '—'}
                          </dd>
                        </div>
                      ))}
                    </dl>

                    <div className='person-detail__stack'>
                      <span className='person-detail__label'>{t('people_col_groups')}</span>
                      {detail.groups.length > 0 ? (
                        <span className='person-detail__chips'>
                          {detail.groups.map((g) => (
                            <Chip key={g.id} variant='info' iconStart='users'>
                              {g.name}
                            </Chip>
                          ))}
                        </span>
                      ) : (
                        <span className='person-detail__value'>—</span>
                      )}
                    </div>
                    <div className='person-detail__stack'>
                      <span className='person-detail__label'>{t('people_detail_tags_label')}</span>
                      {detail.tags.length > 0 ? (
                        <span className='person-detail__chips'>
                          {detail.tags.map((tg) => (
                            <Chip key={tg.id} variant='brand' iconStart='tag'>
                              {tg.title}
                            </Chip>
                          ))}
                        </span>
                      ) : (
                        <span className='person-detail__value'>—</span>
                      )}
                    </div>
                  </>
                )}
              </div>
            </Card>

            {/* ---- company structure card (editable: add/remove placements) ---- */}
            <Card className='person-detail__section'>
              <div className='person-detail__section-head'>
                <span className='person-detail__section-title'>
                  {t('people_detail_structure_title')}
                </span>
                <p className='person-detail__section-subtitle'>
                  {t('people_detail_structure_subtitle')}
                </p>
              </div>
              <div className='person-detail__chips person-detail__chips--wrap'>
                {draft.nodeIds.map((id) => {
                  const node = nodeById.get(id);
                  return (
                    <span key={id} className='person-detail__place-chip'>
                      <Icon name={node?.levelIcon || 'building-2'} size={16} />
                      <span className='person-detail__chip-text'>
                        <span className='person-detail__chip-name'>{node?.name ?? id}</span>
                        {node?.levelName && (
                          <span className='person-detail__chip-sub'>{node.levelName}</span>
                        )}
                      </span>
                      {canEdit && (
                        <button
                          type='button'
                          className='person-detail__chip-x'
                          aria-label={t('people_detail_place_remove', { name: node?.name ?? id })}
                          onClick={() => toggleNode(id)}
                        >
                          <Icon name='x' size={14} />
                        </button>
                      )}
                    </span>
                  );
                })}
                {canEdit && (
                  <Dropdown
                    open={nodePickerOpen}
                    onClose={() => setNodePickerOpen(false)}
                    align='start'
                    portal
                    className='person-detail__picker'
                    trigger={
                      <button
                        type='button'
                        className='people-role-pill people-role-pill--dashed'
                        aria-expanded={nodePickerOpen}
                        onClick={() => setNodePickerOpen((o) => !o)}
                      >
                        <Icon name='plus' size={15} />
                        {t('people_detail_structure_add_place')}
                      </button>
                    }
                  >
                    <div className='person-detail__picker-list'>
                      {allNodes.length === 0 && (
                        <span className='person-detail__picker-empty'>
                          {t('people_detail_structure_empty')}
                        </span>
                      )}
                      {allNodes.map((n) => {
                        const active = draft.nodeIds.includes(n.id);
                        return (
                          <button
                            key={n.id}
                            type='button'
                            role='menuitemcheckbox'
                            aria-checked={active}
                            className={`app-dropdown__item${active ? ' is-selected' : ''}`}
                            onClick={() => toggleNode(n.id)}
                          >
                            <Icon name={n.levelIcon || 'building-2'} size={14} />
                            <span className='app-dropdown__item-label'>{n.name}</span>
                            <Icon name={active ? 'check' : 'plus'} size={16} />
                          </button>
                        );
                      })}
                    </div>
                  </Dropdown>
                )}
              </div>
              {draft.nodeIds.length > 0 && (
                <p className='person-detail__banner'>
                  <Icon
                    name={nodeById.get(draft.nodeIds[0])?.levelIcon || 'building-2'}
                    size={16}
                  />
                  <span>{t('people_detail_structure_reach', { nodes: nodeNames })}</span>
                </p>
              )}
            </Card>

            {/* ---- roles card ---- */}
            <Card className='person-detail__section'>
              <div className='person-detail__section-head'>
                <span className='person-detail__section-title'>
                  {t('people_detail_roles_title')}
                </span>
                <p className='person-detail__section-subtitle'>
                  {t('people_detail_roles_subtitle')}
                </p>
              </div>
              {/* Owner roles are fixed (read-only), and so is everyone's list
                  without the `edit` verb. Others: editable picker with a
                  "at least one role" rule — regardless of the Details edit toggle. */}
              {isOwnerUser || !canEdit ? (
                <div className='person-detail__chips person-detail__chips--wrap'>
                  {detail.roles.map((r) => (
                    <span key={r.id} className='person-detail__role-chip'>
                      <Icon name='shield-check' size={16} />
                      <span className='person-detail__chip-name'>{r.name}</span>
                    </span>
                  ))}
                </div>
              ) : (
                <>
                  {renderChipPicker(
                    draft.roleIds,
                    roleNameById,
                    detailedRoles.map((r) => ({ id: r.id, label: r.name })),
                    'shield-check',
                    rolePickerOpen,
                    setRolePickerOpen,
                    toggleRole,
                    t('people_detail_roles_add'),
                    'people_detail_role_remove',
                    t('people_detail_roles_empty'),
                  )}
                  {rolesInvalid && (
                    <p className='person-detail__error'>
                      <Icon name='circle-alert' size={14} />
                      {t('people_detail_roles_required')}
                    </p>
                  )}
                </>
              )}
            </Card>

            {/* ---- effective access card (read-only) ---- */}
            <Card className='person-detail__section'>
              <div className='person-detail__section-head'>
                <span className='person-detail__access-title-row'>
                  <span className='person-detail__section-title'>
                    {t('people_detail_access_title')}
                  </span>
                  <span className='person-detail__readonly'>
                    <Icon name='lock' size={12} />
                    {t('people_detail_access_readonly')}
                  </span>
                </span>
                <p className='person-detail__section-subtitle'>
                  {t('people_detail_access_subtitle')}
                </p>
              </div>

              <div className='person-detail__summary'>
                <Chip variant='neutral' iconStart='map-pin'>
                  {t('people_detail_structure_count', { count: draft.nodeIds.length })}
                </Chip>
                <Chip variant='neutral' iconStart='shield-check'>
                  {t('people_detail_roles_count', { count: rolesCount })}
                </Chip>
              </div>

              {draft.nodeIds.length > 0 && (
                <p className='person-detail__scope'>
                  {t('people_detail_access_scope', { nodes: nodeNames })}
                </p>
              )}

              {webAccess.length === 0 && mobileAccess.length === 0 ? (
                <p className='person-detail__access-empty'>{t('people_detail_access_empty')}</p>
              ) : (
                <div className='person-detail__access'>
                  {renderAccess(t('people_detail_access_web'), 'monitor', webAccess)}
                  {renderAccess(t('people_detail_access_mobile'), 'smartphone', mobileAccess)}
                </div>
              )}
            </Card>
          </div>
        )
      )}
    </Drawer>
  );
};

export default PersonDetailDrawer;
